/**
 * HMAC-SHA256 utility used by AF-128 to hash BVNs client-side before
 * shipping to the backend. The pepper is session-bound (rotated per login)
 * and fetched once via `/api/v1/me/bvn-pepper`.
 *
 * Web Crypto is required — we deliberately don't ship a JS fallback so any
 * environment without `crypto.subtle` (e.g. an incompatible browser) fails
 * loud rather than silently sending raw BVNs.
 */
export async function hmacSha256Hex(pepper: string, value: string): Promise<string> {
  if (!globalThis.crypto?.subtle) {
    throw new Error(
      'Web Crypto SubtleCrypto unavailable; refusing to hash BVN without it.',
    )
  }
  const enc = new TextEncoder()
  const key = await globalThis.crypto.subtle.importKey(
    'raw',
    enc.encode(pepper),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  )
  const sig = await globalThis.crypto.subtle.sign('HMAC', key, enc.encode(value))
  return [...new Uint8Array(sig)]
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}
